import { useTreatments } from '@splitsoftware/splitio-react';
import type { PageFlagName } from './pageFlags';

export interface UsePageFeatureResult {
  /** True when Split treatment for this flag is 'on' (page/component enabled for current user). */
  enabled: boolean;
  /** Raw treatment value from Split ('on' | 'off' | 'control'). */
  treatment: string;
}

/**
 * Returns whether the given page/component feature flag is enabled for the current user.
 * Use only for temporary page/feature gating; permanent authorization should use C2FO User Roles or Features.
 * The Split key is the authenticated user's UUID (set by AdminSplitProvider).
 *
 * @param flagName - A key from PageFlags (e.g. PageFlags.INSIGHTS_PAGE)
 * @returns { enabled, treatment } - enabled is true when treatment === 'on'
 *
 * @example
 * ```tsx
 * const { enabled } = usePageFeature(PageFlags.INSIGHTS_PAGE);
 * if (!enabled) {
 *   return <ComingSoonPage />;
 * }
 * return <InsightsPage />;
 * ```
 */
export function usePageFeature(flagName: PageFlagName): UsePageFeatureResult {
  const treatments = useTreatments([flagName]);
  const result = treatments[flagName];
  const treatment = result?.treatment ?? 'control';
  const enabled = treatment === 'on';

  return { enabled, treatment };
}
